import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useState, useEffect } from 'react';

export interface UserDataFilter {
  restaurantId: string | null;
  branchIds: string[];
  categoryIds: string[];
}

/**
 * Get the current user's restaurant and branch information for data filtering
 */
export async function getUserDataFilter(userId: string): Promise<UserDataFilter> {
  try {
    // Get user's restaurant
    const { data: restaurantData, error: restaurantError } = await supabase
      .from('restaurants')
      .select('id')
      .eq('owner_id', userId)
      .single();

    if (restaurantError) {
      console.error('Error fetching restaurant:', restaurantError);
      return { restaurantId: null, branchIds: [], categoryIds: [] };
    }

    if (!restaurantData) {
      return { restaurantId: null, branchIds: [], categoryIds: [] };
    }

    const restaurantId = restaurantData.id;

    // Get all branches for this restaurant
    const { data: branchesData, error: branchesError } = await supabase
      .from('branches')
      .select('id')
      .eq('restaurant_id', restaurantId);

    if (branchesError) {
      console.error('Error fetching branches:', branchesError);
      return { restaurantId, branchIds: [], categoryIds: [] };
    }

    const branchIds = branchesData?.map(b => b.id) || [];

    if (branchIds.length === 0) {
      return { restaurantId, branchIds: [], categoryIds: [] };
    }

    // Get all categories for these branches
    const { data: categoriesData, error: categoriesError } = await supabase
      .from('categories')
      .select('id')
      .in('branch_id', branchIds);

    if (categoriesError) {
      console.error('Error fetching categories:', categoriesError);
      return { restaurantId, branchIds, categoryIds: [] };
    }

    const categoryIds = categoriesData?.map(c => c.id) || [];

    return {
      restaurantId,
      branchIds,
      categoryIds
    };
  } catch (error) {
    console.error('Error in getUserDataFilter:', error);
    return { restaurantId: null, branchIds: [], categoryIds: [] };
  }
}

/**
 * Efficient server-side data fetching functions
 */

/**
 * Fetch menu items for the current user's restaurant (server-side filtered)
 */
export async function fetchUserMenuItems(userId: string, branchId?: string) {
  try {
    let categoryIds: string[] = [];
    
    if (branchId) {
      // If branchId is provided, get categories for that specific branch
      const { data: categoriesData, error: categoriesError } = await (supabase as any)
        .from('categories')
        .select('id')
        .eq('branch_id', branchId);
      
      if (categoriesError) {
        console.error('Error fetching categories for branch:', categoriesError);
        return { data: [], error: categoriesError };
      }
      
      categoryIds = categoriesData?.map(c => c.id) || [];
    } else {
      // Use the original method to get all user's categories
      const userFilter = await getUserDataFilter(userId);
      categoryIds = userFilter.categoryIds;
    }
    
    if (!categoryIds.length) {
      return { data: [], error: null };
    }

    const { data, error } = await (supabase as any)
      .from('menu_items')
      .select('*')
      .in('category_id', categoryIds);

    return { data: data || [], error };
  } catch (error) {
    console.error('Error fetching user menu items:', error);
    return { data: [], error };
  }
}

/**
 * Fetch orders for the current user's restaurant (server-side filtered)
 */
export async function fetchUserOrders(userId: string, timeRange?: number, branchId?: string) {
  try {
    let branchIds: string[] = [];
    
    if (branchId) {
      // If branchId is provided, use only that branch
      branchIds = [branchId];
    } else {
      // Use the original method to get all user's branches
      const userFilter = await getUserDataFilter(userId);
      branchIds = userFilter.branchIds;
    }
    
    if (!branchIds.length) {
      return { data: [], error: null };
    }

    let query = (supabase as any)
      .from('orders')
      .select('*, order_items(*)')
      .in('branch_id', branchIds);

    if (timeRange) {
      const cutoffDate = new Date(Date.now() - timeRange * 24 * 60 * 60 * 1000).toISOString();
      query = query.gte('created_at', cutoffDate);
    }

    const { data, error } = await query;

    return { data: data || [], error };
  } catch (error) {
    console.error('Error fetching user orders:', error);
    return { data: [], error };
  }
}

/**
 * Fetch recipes for the current user (already filtered by user_id)
 */
export async function fetchUserRecipes(userId: string, branchId?: string) {
  try {
    let query = (supabase as any)
      .from('recipes')
      .select('*, recipe_ingredients(*)')
      .eq('user_id', userId);

    if (branchId) {
      query = query.eq('branch_id', branchId);
    }

    const { data, error } = await query;

    return { data: data || [], error };
  } catch (error) {
    console.error('Error fetching user recipes:', error);
    return { data: [], error };
  }
}

/**
 * Fetch raw materials for the current user (already filtered by user_id)
 */
export async function fetchUserMaterials(userId: string, branchId?: string) {
  try {
    let query = (supabase as any)
      .from('raw_materials')
      .select('*')
      .eq('user_id', userId);

    if (branchId) {
      query = query.eq('branch_id', branchId);
    }

    const { data, error } = await query;

    return { data: data || [], error };
  } catch (error) {
    console.error('Error fetching user materials:', error);
    return { data: [], error };
  }
}

/**
 * Fetch inventory transactions for the current user (already filtered by user_id)
 */
export async function fetchUserTransactions(userId: string, timeRange?: number, branchId?: string) {
  try {
    let query = (supabase as any)
      .from('inventory_transactions')
      .select('*')
      .eq('user_id', userId);

    if (branchId) {
      query = query.eq('branch_id', branchId);
    }

    if (timeRange) {
      const cutoffDate = new Date(Date.now() - timeRange * 24 * 60 * 60 * 1000).toISOString();
      query = query.gte('created_at', cutoffDate);
    }

    const { data, error } = await query;

    return { data: data || [], error };
  } catch (error) {
    console.error('Error fetching user transactions:', error);
    return { data: [], error };
  }
}

/**
 * Hook to get user data filter for React components
 */
export function useUserDataFilter() {
  const { user } = useAuth();
  const [filter, setFilter] = useState<UserDataFilter>({ 
    restaurantId: null, 
    branchIds: [], 
    categoryIds: [] 
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      getUserDataFilter(user.id).then(setFilter).finally(() => setLoading(false));
    } else {
      setFilter({ restaurantId: null, branchIds: [], categoryIds: [] });
      setLoading(false);
    }
  }, [user]);

  return { filter, loading };
} 